<script setup>
/**
 * A text field with an optional "write this better for me" assist — and the
 * preview-then-accept gate that makes the assist safe. THE PLAN, §4 P3.
 *
 * THE RULE THIS COMPONENT EXISTS TO ENFORCE: the model never edits the owner's words in
 * place. A suggestion arrives in its own panel, beside the original, and replaces
 * nothing until the owner presses the accept button. If she presses discard, or navigates
 * away, or the request fails, the field is byte-for-byte what she typed. That is the
 * whole difference between an assistant and an autocorrect nobody asked for.
 *
 * The assist is hidden until there is enough text to improve, because a button offering
 * to rewrite an empty box is noise on a page that is already asking for four answers.
 *
 * WHERE THE ASSIST LINK SITS, AND WHY IT MOVED. It used to share a row with the hint
 * under the field. A blind critic on the dark shot: "the hint is ~9px dim grey sharing a
 * cramped baseline with the 'Suggest better wording' link". Two things of different
 * weight on one baseline read as one thing, badly. The link is an affordance belonging to
 * the field, so it now sits on the field's LABEL row — right-aligned, opposite the label
 * it acts on — and the hint has the line above the box to itself.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import WasloTextArea from './WasloTextArea.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import AiConfirmDialog from './AiConfirmDialog.vue';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { wasloApi, withConfirm } from '../api/client.js';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  /** What to write in the box. Rendered above it, at reading size. */
  hint: { type: String, default: '' },
  /** Validation or assist failure. Rendered under the field, where you look after acting. */
  message: { type: String, default: '' },
  messageType: { type: String, default: 'error' },
  /** Forwarded to the BFF; it echoes it back so two in-flight suggestions cannot swap. */
  kind: { type: String, default: 'persona' },
  /** Extra sentence shown inside the preview panel, set by the parent per suggestion. */
  previewNote: { type: String, default: '' },
  minHeight: { type: String, default: '5rem' },
  autofocus: { type: Boolean, default: false },
  /** Below this many characters there is nothing worth suggesting, so no button. */
  assistThreshold: { type: Number, default: 20 },
});

const emit = defineEmits(['update:modelValue', 'suggestion', 'accepted']);

const { t } = useWasloI18n();
const accountId = useWasloAccountId();

// P-5.12: the price is on the link, the owner confirms it in one dialog, and the server
// charges exactly that. `receiptText` is top-level so the template unwraps it.
const assist = useAiConfirm({ accountId, op: 'suggest_wording' });
const { receiptText } = assist;
const assistLabel = computed(() =>
  assist.label(t('WASLO.PERSONALITY.ASSIST.ACTION'))
);

const suggestion = ref('');
const isWorking = ref(false);
const assistError = ref('');

const canAssist = computed(
  () => (props.modelValue ?? '').trim().length >= props.assistThreshold
);

const requestSuggestion = async () => {
  if (isWorking.value) return;
  const text = (props.modelValue ?? '').trim();
  if (!text) return;

  isWorking.value = true;
  assistError.value = '';
  try {
    const outcome = await assist.confirmAndRun({
      run: credits =>
        wasloApi.optimize(
          accountId.value,
          { kind: props.kind, text },
          undefined,
          withConfirm(credits)
        ),
    });
    // Cancel costs nothing and changes nothing: her words are exactly as she left them.
    if (outcome.cancelled) return;
    suggestion.value = outcome.result.text;
    emit('suggestion', outcome.result.text);
  } catch {
    // Deliberately one message for every failure mode: the owner's next move is the
    // same whichever it was, and her text is untouched in all of them.
    assistError.value = t('WASLO.PERSONALITY.ASSIST.FAILED');
    suggestion.value = '';
    emit('suggestion', '');
  } finally {
    isWorking.value = false;
  }
};

const accept = () => {
  const accepted = suggestion.value;
  suggestion.value = '';
  emit('update:modelValue', accepted);
  emit('accepted', accepted);
  emit('suggestion', '');
};

const discard = () => {
  suggestion.value = '';
  assistError.value = '';
  emit('suggestion', '');
};

defineExpose({ requestSuggestion, isWorking });
</script>

<template>
  <div class="flex flex-col gap-3">
    <WasloTextArea
      :model-value="modelValue"
      :label="label"
      :hint="hint"
      :placeholder="placeholder"
      :min-height="minHeight"
      :autofocus="autofocus"
      :message="assistError || message"
      :message-type="assistError ? 'error' : messageType"
      @update:model-value="emit('update:modelValue', $event)"
    >
      <template #labelAction>
        <Button
          v-if="canAssist && !suggestion"
          :label="assistLabel"
          icon="i-lucide-sparkles"
          size="sm"
          variant="link"
          color="slate"
          class="flex-shrink-0"
          :is-loading="isWorking"
          @click="requestSuggestion"
        />
      </template>
    </WasloTextArea>
    <p
      v-if="receiptText"
      class="mb-0 text-xs text-n-slate-11"
      data-waslo-ai-receipt
    >
      {{ receiptText }}
    </p>

    <section
      v-if="suggestion"
      class="flex flex-col gap-3 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
      data-waslo-suggestion
    >
      <div class="flex flex-col gap-1">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('WASLO.PERSONALITY.ASSIST.PREVIEW_TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-11">
          {{ previewNote || t('WASLO.PERSONALITY.ASSIST.PREVIEW_NOTE') }}
        </p>
      </div>

      <p
        dir="auto"
        class="p-3 text-sm leading-6 whitespace-pre-wrap rounded-lg text-n-slate-12 bg-n-alpha-black2"
      >
        {{ suggestion }}
      </p>

      <div class="flex gap-2">
        <Button
          :label="t('WASLO.PERSONALITY.ASSIST.ACCEPT')"
          size="sm"
          @click="accept"
        />
        <Button
          :label="t('WASLO.PERSONALITY.ASSIST.DISCARD')"
          size="sm"
          variant="faded"
          color="slate"
          @click="discard"
        />
      </div>
    </section>
    <AiConfirmDialog :controller="assist" />
  </div>
</template>
